/**
 * Hooks para manejar queries y mutaciones de interacciones usando TanStack Query.
 * Proporciona funciones para obtener interacciones y reiniciar contadores.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';
import { getMyInteractionsList, getMyVisits } from '../services/interactionsService';

/**
 * Hook para obtener la lista de interacciones del usuario.
 * Usa queryKey ['interactions'].
 * Se habilita siempre, pero maneja errores en el servicio.
 * 
 * @returns {Object} - Objeto con data, isLoading, refetch, error
 */
export const useInteractionsQuery = () => {
  return useQuery({
    queryKey: ['interactions'],
    queryFn: getMyInteractionsList,
    staleTime: 2 * 60 * 1000, // 2 minutos
  });
};

/**
 * Hook para reiniciar los contadores de interacciones del usuario.
 * Usa el queryClient para invalidar queries automáticamente después de actualizar.
 * 
 * @param {string} userId - ID del usuario (opcional, si no se proporciona usa useAuth)
 * @returns {Object} - Objeto con mutate, isPending, error, etc.
 */
export const useResetCountersMutation = (userId = null) => {
  const queryClient = useQueryClient();
  const hookUserId = userId;

  return useMutation({
    mutationFn: async () => {
      console.log('[useResetCountersMutation] Iniciando mutación con userId:', hookUserId);
      
      if (!hookUserId) {
        console.error('[useResetCountersMutation] userId es undefined, no se puede reiniciar contadores');
        return Promise.reject(new Error('userId es undefined'));
      }

      try {
        const result = await getMyVisits(hookUserId, queryClient);
        console.log('[useResetCountersMutation] Contadores reiniciados exitosamente');
        return result;
      } catch (err) {
        console.error('[useResetCountersMutation] Error en mutación:', err);
        throw err;
      }
    },
    onSuccess: (result) => {
      console.log('[useResetCountersMutation] onSuccess - result:', result);
      if (result) {
        console.log('[useResetCountersMutation] Contadores reiniciados exitosamente');
      }
    },
    onError: (error) => {
      console.error('[useResetCountersMutation] onError - error:', error);
    },
    onMutate: () => {
      console.log('[useResetCountersMutation] onMutate - Iniciando mutación');
    },
    onSettled: () => {
      console.log('[useResetCountersMutation] onSettled - Mutación finalizada');
    },
    enabled: !!hookUserId,
  });
};
